import { FC, MouseEvent, useCallback, useMemo, useRef } from 'react';
import {
    SheetFilterStateReduced,
    SheetFilterData,
    TileDataDimensionSlicer,
    TileDataSheetBackedWithSheet,
    TileData,
    TileRepresentation,
    TileVisualizationType,
    TileStyle,
} from '@common/api';
import { useI18nContext } from '@i18n/i18n-react';
import { Update } from '@use-gpu/state';

import {
    ActionButtonsContainer,
    DashboardTileCard,
    DashboardTileTitle,
    DashboardTileSubtitle,
    DashboardTileFootnotes,
} from './Dashboard.style';
import { ButtonSelectOption } from '@components/button-select/ButtonSelect';
import {} from '@components/grid-layout/validateLayout';
import {
    GridLayoutTile,
    GridLayout,
    GridLayoutAbsoluteGrid,
    GridLayoutBreakpoint,
    GridLayoutIgnoreDragContainer,
} from '@components/grid-layout';
import { SheetTile } from './tiles/SheetTile';
import { ChartTile } from './tiles/ChartTile';
import { SingleValueTile } from './tiles/SingleValueTile';
import { TableTile, TableTileRef } from './tiles/TableTile';
import { TextTile } from './tiles/TextTile';
import { IsolateAbsolute } from '@components/layout/Flex';
import { TileOption } from './Dashboard';

import { ContextMenu, useContextMenuState } from '@components/context-menu/ContextMenu';
import {
    Plus,
    Edit,
    Copy,
    Delete,
    ThreeDots,
    Link,
    Download,
    Close,
    ChevronRightDouble,
    ChevronDownDouble,
} from '@components/icons/FarseerIcons';
import { Checked, MixChecked, UnChecked } from '@components/icons/FarseerIcons';
import { TileDataType } from '@util/enums';
import { DimensionSlicerTile } from './tiles/DimensionSlicerTile';
import { $replaceItem } from '@util/patch';
import { Button } from '@components/button';
import { CloneTooltipWrapper } from '@components/tooltip/Tooltip';
import { useHover } from '@hooks/useHover';
import { DividerVertical } from '@components/layout/Divider';

const MIN_WIDTH = 120;

export const GRID_LAYOUT_AUTO_COLUMNS = 48;

export const GRID_LAYOUT_BREAKPOINTS: Record<string, GridLayoutBreakpoint> = {
    large: {
        width: 900,
        columns: 12,
        rowHeight: 30,
        minW: 2,
        minH: 3,
    },
    small: {
        width: 768,
        columns: 6,
        rowHeight: 30,
        minW: 1,
        minH: 3,
    },
};

export const GRID_LAYOUT_ABSOLUTE: GridLayoutAbsoluteGrid = {
    columnWidth: 30,
    rowHeight: 30,
    minW: 3,
    minH: 3,
};

const NO_LAYOUT = { x: 0, y: 0, w: 5, h: 4 };

export type DashboardGridProps = {
    tiles: TileRepresentation[];
    isEditingTiles: boolean;

    focusedTileId: number | null;
    selectedTileId: number | null;

    gridLocked: boolean;
    responsiveLayout: boolean;

    onLayoutChange: (update: Update<TileRepresentation[]>, breakpoint: string) => void;
    onBreakpointChange: (breakpoint: string) => void;

    newTileOptions: ButtonSelectOption<TileOption>[];
    onTileNew: (option: TileOption) => void;

    onTileUpdate: (tile: TileRepresentation, update: Update<TileRepresentation>) => void;
    onTileEdit: (tileId: number | null) => void;
    onTileFocus: (tileId: number | null) => void;
    onTileSelect: (tileId: number | null) => void;
    onTileDuplicate: (tile: TileRepresentation) => void;
    onTileCopyToDashboard: (tile: TileRepresentation) => void;
    onTileDelete: (tileId: number) => void;

    canEdit: boolean;
    canDataEntry: boolean;

    generalFilters: SheetFilterStateReduced[];
    updateFilters: (update: Update<SheetFilterData[]>) => void;
};

export const DashboardGrid: FC<DashboardGridProps> = (props: DashboardGridProps) => {
    const {
        tiles,
        onLayoutChange,
        onBreakpointChange,
        newTileOptions,
        onTileNew,
        onTileSelect,
        onTileFocus,
        onTileEdit,
        onTileUpdate,
        onTileDuplicate,
        onTileCopyToDashboard,
        onTileDelete,
        focusedTileId,
        selectedTileId,
        gridLocked,
        responsiveLayout,
        isEditingTiles,
        canEdit,
        canDataEntry,
        generalFilters,
        updateFilters,
    } = props;

    const layouts = useMemo(() => resolveLayouts(tiles), [tiles]);
    const styles = useMemo(() => {
        const styles: Record<string, TileStyle> = {};
        tiles.forEach((tile) => {
            styles[tile.id] = tile.style;
        });
        return styles;
    }, [tiles]);

    const handleLayoutChange = useCallback(
        (layout: GridLayoutTile[], breakpoint: string) => {
            const update: Update<TileRepresentation[]> = {};

            let i = 0;
            for (const { x, y, w, h } of layout) {
                update[i++] = { gridPlacements: { [breakpoint]: { x, y, w, h } } } as Update<TileRepresentation>;
            }

            onLayoutChange(update, breakpoint);
        },
        [onLayoutChange]
    );

    const { menuState, menuHandlers, clickAnchorRef } = useContextMenuState({ click: 'right' });
    const contextMenuItems = newTileOptions.map((option) => (
        <ContextMenu.Item
            key={option.label}
            item={{
                icon: Plus,
                label: option.label,
                disabled: !canEdit,
                action: () => onTileNew(option.value),
            }}
        />
    ));

    const contextMenu =
        focusedTileId == null ? (
            <ContextMenu {...menuState} anchorRef={clickAnchorRef}>
                {contextMenuItems}
            </ContextMenu>
        ) : null;

    return (
        <>
            {contextMenu}
            <GridLayout
                layouts={layouts}
                styles={styles}
                breakpoints={responsiveLayout ? GRID_LAYOUT_BREAKPOINTS : undefined}
                absoluteGrid={GRID_LAYOUT_ABSOLUTE}
                focusedTileId={focusedTileId}
                onTileSelect={onTileSelect}
                onTileEdit={onTileEdit}
                onLayoutChange={handleLayoutChange}
                onBreakpointChange={onBreakpointChange}
                isDraggable={!gridLocked && !focusedTileId && canEdit}
                isResizable={!gridLocked && !focusedTileId && canEdit}
                menuHandlers={menuHandlers}
            >
                {(index: number) => {
                    const tile = tiles[index];
                    return (
                        <DashboardTile
                            tile={tile}
                            gridLocked={gridLocked}
                            canEdit={canEdit}
                            canDataEntry={canDataEntry}
                            focusedTileId={focusedTileId}
                            selectedTileId={selectedTileId}
                            isEditingTiles={isEditingTiles}
                            onTileUpdate={onTileUpdate}
                            onTileEdit={onTileEdit}
                            onTileFocus={onTileFocus}
                            onTileSelect={onTileSelect}
                            onTileDuplicate={onTileDuplicate}
                            onTileCopyToDashboard={onTileCopyToDashboard}
                            onTileDelete={onTileDelete}
                            generalFilters={generalFilters}
                            updateFilters={updateFilters}
                        />
                    );
                }}
            </GridLayout>
        </>
    );
};

export const resolveLayouts = (tiles: TileRepresentation[]): Record<string, GridLayoutTile[]> => {
    const absolutes = []; // absolute
    const larges = []; // responsive large
    const smalls = []; // responsive small

    for (const tile of tiles) {
        const { id, gridPlacements } = tile;

        const { absolute, large, small } = gridPlacements ?? {};

        let a = absolute;
        let l = large;
        let s = small;

        if (absolute) {
            if (!l) l = absoluteToLarge(absolute);
            if (!s) s = absoluteToSmall(absolute);
        }
        if (large) {
            if (!a) a = largeToAbsolute(large);
            if (!s) s = largeToSmall(large);
        }
        if (small) {
            if (!a) a = smallToAbsolute(small);
            if (!l) l = smallToLarge(small);
        }

        absolutes.push({ id, ...(a ?? NO_LAYOUT) });
        larges.push({ id, ...(l ?? NO_LAYOUT) });
        smalls.push({ id, ...(s ?? NO_LAYOUT) });
    }

    return { absolute: absolutes, large: larges, small: smalls };
};

// Migrate between grid styles on demand
type GridDimensions = { x: number; y: number; w: number; h: number };

// Clip to small size (and pushdownwards if wrapped)
const clipSmall = ({ x, y, w, h }: GridDimensions) => {
    const ww = Math.min(6, w);
    const xx = Math.min(6 - ww, x);

    const s = Math.floor((x - xx) / 6);
    const yy = y + s;

    return { x: xx, y: yy, w: ww, h };
};

// Clip to large size (and pushdownwards if wrapped)
const clipLarge = ({ x, y, w, h }: GridDimensions) => {
    const ww = Math.min(12, w);
    const xx = Math.min(12 - ww, x);

    const s = Math.floor((x - xx) / 12);
    const yy = y + s;

    return { x: xx, y: yy, w: ww, h };
};

const absoluteToSmall = ({ x, y, w, h }: GridDimensions) =>
    clipSmall({ x: Math.ceil(x / 2), y, w: Math.ceil(w / 2), h });
const absoluteToLarge = ({ x, y, w, h }: GridDimensions) =>
    clipLarge({ x: Math.ceil(x / 4), y, w: Math.ceil(w / 4), h });

const smallToAbsolute = ({ x, y, w, h }: GridDimensions) => ({ x: x * 2, y, w: w * 2, h });
const largeToAbsolute = ({ x, y, w, h }: GridDimensions) => ({ x: x * 4, y, w: w * 4, h });

const smallToLarge = ({ x, y, w, h }: GridDimensions) => ({ x: x * 2, y, w: w * 2, h });
const largeToSmall = ({ x, y, w, h }: GridDimensions) => clipSmall({ x, y, w, h });

function DashboardTile({
    tile,
    focusedTileId,
    selectedTileId,
    isEditingTiles,
    onTileUpdate,
    onTileEdit,
    onTileFocus,
    onTileSelect,
    onTileDuplicate,
    onTileCopyToDashboard,
    onTileDelete,
    gridLocked,
    canEdit,
    canDataEntry,
    generalFilters,
    updateFilters,
}: {
    tile: TileRepresentation;
    focusedTileId: number | null;
    selectedTileId: number | null;
    isEditingTiles: boolean;
    onTileUpdate: (tile: TileRepresentation, update: Update<TileRepresentation>) => void;
    onTileEdit: (tileId: number) => void;
    onTileFocus: (tileId: number | null) => void;
    onTileSelect: (tileId: number | null) => void;
    onTileDuplicate: (tile: TileRepresentation) => void;
    onTileCopyToDashboard: (tile: TileRepresentation) => void;
    onTileDelete: (tileId: number) => void;
    gridLocked: boolean;
    canEdit: boolean;
    canDataEntry: boolean;
    generalFilters: SheetFilterStateReduced[];
    updateFilters: (update: Update<SheetFilterData[]>) => void;
}) {
    const { LL } = useI18nContext();

    const tableTileRef = useRef<TableTileRef>(null);

    const btnRef = useRef(null);
    const [hovered, handlers] = useHover();

    const backgroundColor = tile.style.showBackground ? tile.style.backgroundColor : '#ffffff';

    const updateTileData = useCallback(
        (update: Update<TileData>) => {
            if (tile) {
                const op: Update<TileRepresentation> = { data: update };
                onTileUpdate(tile, op);
            }
        },
        [onTileUpdate, tile]
    );

    function getDashboardTileContent(tile: TileRepresentation) {
        switch (tile.data.type) {
            case 'TEXT':
                return (
                    <TextTile
                        data={tile.data}
                        isEditing={isEditing}
                        canEdit={canEdit}
                        canDataEntry={canDataEntry}
                        updateTileData={updateTileData}
                    />
                );
            case 'DIMENSION_SLICER':
                return (
                    <DimensionSlicerTile
                        data={tile.data}
                        canEdit={canEdit}
                        filter={generalFilters.find(
                            (f) => f.tableId === (tile.data as TileDataDimensionSlicer).dimensionTableId
                        )}
                        updateFilters={updateFilters}
                    />
                );
            case 'SHEET_BACKED':
                return getSheetBackedTileContent(tile.name, tile.data, tile.style);
            default:
                return null;
        }
    }

    function getSheetBackedTileContent(title: string, data: TileDataSheetBackedWithSheet, style: TileStyle) {
        switch (data.visualization.type) {
            case TileVisualizationType.Sheet:
                return <SheetTile title={title} data={data} canEdit={canEdit} canDataEntry={canDataEntry} />;
            case TileVisualizationType.Table:
                return (
                    <TableTile
                        ref={tableTileRef}
                        data={data}
                        updateTileData={updateTileData}
                        canEdit={canEdit}
                        canDataEntry={canDataEntry}
                        hasTitle={hasTitle}
                        style={style}
                    />
                );
            case TileVisualizationType.Chart:
                return (
                    <ChartTile
                        data={data}
                        background={backgroundColor}
                        canEdit={canEdit}
                        canDataEntry={canDataEntry}
                        hasTitle={hasTitle}
                    />
                );
            case TileVisualizationType.SingleValue:
                return <SingleValueTile data={data} canEdit={canEdit} canDataEntry={canDataEntry} />;
            default:
                return null;
        }
    }

    const filterTableId = tile.data.type === TileDataType.DIMENSION_SLICER ? tile.data.dimensionTableId : null;
    const filter = filterTableId ? generalFilters.find((f) => f.tableId === filterTableId) : null;
    const filterNumOfSelected = useMemo(() => {
        return filter?.dimensions.reduce((memo, f) => {
            if (f.selected) {
                memo += 1;
            }
            return memo;
        }, 0);
    }, [filter]);

    const handleClick = (e: MouseEvent) => {
        e.stopPropagation();
        if (canEdit) {
            onTileSelect(tile.id);
        }
    };

    const openMoreOptions = () => {
        menuState.setOpen(true);
    };

    const isEditing = tile.id === selectedTileId;
    const isFocused = tile.id === focusedTileId;
    const isDimmed = isEditingTiles && !isEditing;
    const visualisationType = (tile.data as TileDataSheetBackedWithSheet)?.visualization?.type;
    const isDisabled = !canEdit || gridLocked;

    const hasTitle = tile.name !== '' || (tile.subtitle !== '' && tile.style.showSubtitle);

    const { menuState } = useContextMenuState({ click: 'left' });
    const { menuState: rightClickMenuState, menuHandlers, clickAnchorRef } = useContextMenuState({ click: 'right' });

    const moreOptionsContextMenuItems = (
        <>
            {!isDisabled && (
                <ContextMenu.Item
                    item={{
                        label: LL.EDIT_TILE(),
                        icon: Edit,
                        action: () => onTileEdit(tile.id),
                    }}
                />
            )}
            <ContextMenu.Item
                item={{
                    label: isFocused ? LL.UNFOCUS_TILE() : LL.FOCUS_TILE(),
                    icon: isFocused ? Close : Link,
                    action: () => {
                        if (isFocused) {
                            onTileFocus(null);
                        } else {
                            onTileFocus(tile.id);
                        }
                    },
                }}
            />
            {(!isDisabled || visualisationType === TileVisualizationType.Table) && <ContextMenu.Divider />}

            {!isDisabled && (
                <>
                    {tile.data.type === TileDataType.DIMENSION_SLICER ? (
                        <ContextMenu.Item
                            item={{
                                label: LL.SELECT_ALL(),
                                icon:
                                    filterNumOfSelected === 0
                                        ? UnChecked
                                        : filterNumOfSelected === filter?.dimensions.length
                                        ? Checked
                                        : MixChecked,
                                disabled: !filter?.multiselect,
                                action: () => {
                                    if (filterTableId && filter) {
                                        updateFilters(
                                            $replaceItem((f) => f.tableId === filterTableId, {
                                                tableId: filterTableId,
                                                dimensionIds: [],
                                                selectAll: filterNumOfSelected !== filter?.dimensions.length,
                                                multiselect: filter.multiselect,
                                                selectBlanks: filter.selectBlanks,
                                                blanks: filter.blanks,
                                                displayColumns: filter.displayColumns,
                                            })
                                        );
                                    }
                                },
                            }}
                        />
                    ) : (
                        <>
                            <ContextMenu.Item
                                item={{
                                    label: LL.DUPLICATE(),
                                    icon: Copy,
                                    action: () => onTileDuplicate(tile),
                                }}
                            />
                            <ContextMenu.Item
                                item={{
                                    label: LL.COPY_TO_DASHBOARD() + '...',
                                    icon: Copy,
                                    action: () => onTileCopyToDashboard(tile),
                                }}
                            />
                        </>
                    )}
                </>
            )}
            {visualisationType === TileVisualizationType.Table && (
                <ContextMenu.Item
                    item={{
                        label: LL.EXPORT(),
                        icon: Download,
                        action: tableTileRef.current?.exportTable,
                    }}
                />
            )}

            {!isDisabled && (
                <>
                    <ContextMenu.Divider />
                    <ContextMenu.Item
                        item={{
                            label: LL.DELETE_TILE(),
                            icon: Delete,
                            action: () => onTileDelete(tile.id),
                        }}
                    />
                </>
            )}
        </>
    );

    const hoverToolbar = (
        <ActionButtonsContainer
            style={{
                backgroundColor,
            }}
        >
            {!isDisabled && (
                <CloneTooltipWrapper label={LL.EDIT_TILE()}>
                    <Button
                        icon={Edit}
                        onClick={() => onTileEdit(tile.id)}
                        $type="quaternary"
                        style={{
                            width: 24,
                            height: 24,
                        }}
                    />
                </CloneTooltipWrapper>
            )}

            {visualisationType === TileVisualizationType.Table && (
                <>
                    <CloneTooltipWrapper label={LL.COLLAPSE_TOOLTIP()}>
                        <Button
                            icon={ChevronRightDouble}
                            onClick={tableTileRef.current?.collapseAll}
                            $type="quaternary"
                            style={{
                                width: 24,
                                height: 24,
                            }}
                        />
                    </CloneTooltipWrapper>
                    <CloneTooltipWrapper label={LL.EXPAND_TOOLTIP()}>
                        <Button
                            icon={ChevronDownDouble}
                            onClick={tableTileRef.current?.expandAll}
                            $type="quaternary"
                            style={{
                                width: 24,
                                height: 24,
                            }}
                        />
                    </CloneTooltipWrapper>
                </>
            )}

            <CloneTooltipWrapper label={isFocused ? LL.UNFOCUS_TILE() : LL.FOCUS_TILE()}>
                <Button
                    icon={isFocused ? Close : Link}
                    onClick={() => {
                        handlers.onMouseLeave();
                        if (isFocused) onTileFocus(null);
                        else onTileFocus(tile.id);
                    }}
                    $type="quaternary"
                    style={{
                        width: 24,
                        height: 24,
                    }}
                />
            </CloneTooltipWrapper>
            {(!isDisabled || visualisationType === TileVisualizationType.Table) && (
                <>
                    <DividerVertical $padding={0} />
                    <CloneTooltipWrapper label={LL.MORE_OPTIONS()}>
                        <div ref={btnRef}>
                            <Button
                                icon={ThreeDots}
                                onClick={openMoreOptions}
                                ref={btnRef}
                                $type="quaternary"
                                style={{
                                    width: 24,
                                    height: 24,
                                }}
                            />
                            <ContextMenu
                                {...menuState}
                                anchorRef={btnRef}
                                placement="bottom-end"
                                offsetMain={5}
                                minWidth={MIN_WIDTH}
                            >
                                {moreOptionsContextMenuItems}
                            </ContextMenu>
                        </div>
                    </CloneTooltipWrapper>
                </>
            )}
        </ActionButtonsContainer>
    );

    return (
        <>
            <GridLayoutIgnoreDragContainer>
                <ContextMenu {...rightClickMenuState} anchorRef={clickAnchorRef} minWidth={MIN_WIDTH}>
                    {moreOptionsContextMenuItems}
                </ContextMenu>
            </GridLayoutIgnoreDragContainer>
            <DashboardTileCard
                {...menuHandlers}
                className="tiny-scrollbars only-on-hover"
                $editing={isEditing}
                $dimmed={isDimmed}
                onClick={handleClick}
                $style={tile.style}
                onMouseEnter={() => handlers.onMouseEnter()}
                onMouseLeave={() => {
                    handlers.onMouseLeave();
                    menuState.setOpen(false);
                }}
            >
                {tile.name !== '' &&
                visualisationType !== TileVisualizationType.Sheet &&
                (tile.data.type === TileDataType.DIMENSION_SLICER || tile.data.type === TileDataType.SHEET_BACKED) ? (
                    <DashboardTileTitle>{tile.name}</DashboardTileTitle>
                ) : null}
                {tile.subtitle !== '' &&
                visualisationType !== TileVisualizationType.Sheet &&
                tile.style.showSubtitle ? (
                    <DashboardTileSubtitle>{tile.subtitle}</DashboardTileSubtitle>
                ) : null}
                {getDashboardTileContent(tile)}
                {tile.footnotes !== '' &&
                visualisationType !== TileVisualizationType.Sheet &&
                tile.style.showFootnotes ? (
                    <DashboardTileFootnotes>{tile.footnotes}</DashboardTileFootnotes>
                ) : null}
                {isDimmed ? <IsolateAbsolute /> : null}
                {hovered && !isDimmed && hoverToolbar}
            </DashboardTileCard>
        </>
    );
}
